import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Search, Filter, Download, PlusCircle, CheckSquare, ListFilter } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Checkbox } from "@/components/ui/checkbox";

interface Solution {
  id: string;
  title: string;
  channel: string;
  curator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake";
  category: string;
  price: number;
  description: string;
}

const channelSolutionsData: Record<string, Solution[]> = {
  "curt": [
    {
      id: "curt-001",
      title: "Modular Construction Implementation Guide",
      channel: "curt",
      curator: "Rachel Waters",
      commercialType: "Sale",
      category: "Construction Methods",
      price: 3500,
      description: "Comprehensive guide for implementing modular construction techniques in commercial projects."
    },
    {
      id: "curt-002",
      title: "Prefabrication Best Practices Framework",
      channel: "curt",
      curator: "Jim Collins",
      commercialType: "Lease",
      category: "Construction Methods",
      price: 2800,
      description: "Structured framework for prefabrication implementation with case studies and ROI analysis."
    },
    {
      id: "curt-003",
      title: "Construction Safety Protocol System",
      channel: "curt",
      curator: "Sarah Johnson",
      commercialType: "Collaborate",
      category: "Health & Safety",
      price: 1950,
      description: "Comprehensive safety protocols for modern construction environments."
    },
    {
      id: "curt-004",
      title: "Project Management Excellence Framework",
      channel: "curt",
      curator: "Mike Mosher",
      commercialType: "Sale",
      category: "Project Management",
      price: 4200,
      description: "Specialized knowledge base for construction project management optimization."
    },
    {
      id: "curt-005",
      title: "Green Building Implementation System",
      channel: "curt",
      curator: "Morgan Chen",
      commercialType: "Stake",
      category: "Green Building",
      price: 5800,
      description: "Complete system for implementing sustainable practices in construction projects."
    },
    {
      id: "curt-006",
      title: "BIM Integration Toolkit",
      channel: "curt",
      curator: "Trey Fleming",
      commercialType: "Lease",
      category: "Automation",
      price: 3200,
      description: "Tools and methodologies for seamless BIM integration in construction workflows."
    },
    {
      id: "curt-007",
      title: "Engineering & Design Standards",
      channel: "curt",
      curator: "Karen Thompson",
      commercialType: "Sale",
      category: "Engineering & Design",
      price: 2950,
      description: "Comprehensive design standards and engineering best practices for construction."
    },
    {
      id: "curt-008",
      title: "Construction Data Analytics Platform",
      channel: "curt",
      curator: "Jim Collins",
      commercialType: "Collaborate",
      category: "Data Analytics",
      price: 4500,
      description: "Advanced analytics platform for construction project data optimization."
    }
  ],
  "chevron": [
    {
      id: "chv-001",
      title: "Upstream Operations Optimization",
      channel: "chevron",
      curator: "Morgan Chen",
      commercialType: "Sale",
      category: "Upstream Operations",
      price: 6500,
      description: "Comprehensive knowledge base for optimizing upstream oil and gas operations."
    },
    {
      id: "chv-002",
      title: "Downstream Process Efficiency Framework",
      channel: "chevron",
      curator: "Trey Fleming",
      commercialType: "Lease",
      category: "Downstream Operations",
      price: 5800,
      description: "Framework for improving efficiency in downstream oil and gas processing."
    },
    {
      id: "chv-003",
      title: "Midstream Transportation Best Practices",
      channel: "chevron",
      curator: "Karen Thompson",
      commercialType: "Collaborate",
      category: "Midstream Operations",
      price: 4200,
      description: "Best practices for optimizing midstream oil and gas transportation."
    }
  ],
  "ispai": [
    {
      id: "isp-001",
      title: "AI Ethics Implementation Guide",
      channel: "ispai",
      curator: "Dr. Julia DeRosi",
      commercialType: "Sale",
      category: "AI Ethics",
      price: 4500,
      description: "Comprehensive guide for implementing ethical AI practices across organizations."
    },
    {
      id: "isp-002",
      title: "Machine Learning Operations Framework",
      channel: "ispai",
      curator: "Shaan Patel",
      commercialType: "Lease",
      category: "Machine Learning",
      price: 3800,
      description: "Framework for streamlining ML operations and deployment."
    }
  ]
};

const ChannelSolutions = () => {
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get("channel") || "curt";
  
  const [commercialType, setCommercialType] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedCurator, setSelectedCurator] = useState<string>("all");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("list");
  
  const availableSolutions = channelSolutionsData[channelParam] || [];

  const categories = ["all", ...new Set(availableSolutions.map(solution => solution.category))];
  const curators = ["all", ...new Set(availableSolutions.map(solution => solution.curator))];

  const channelTitles: Record<string, { title: string, description: string }> = {
    "curt": {
      title: "CURT Marketplace",
      description: "Browse 157 specialized knowledge bases on construction methods and best practices"
    },
    "chevron": {
      title: "Chevron Marketplace",
      description: "Browse 186 specialized knowledge bases on oil and gas operations"
    },
    "ispai": {
      title: "iSPAI Marketplace",
      description: "Browse 142 specialized AI and data science knowledge bases"
    }
  };

  const { title, description } = channelTitles[channelParam] || channelTitles["curt"];

  const filteredSolutions = availableSolutions.filter((solution) => {
    return (
      (commercialType === "all" || solution.commercialType === commercialType) &&
      (selectedCategory === "all" || solution.category === selectedCategory) &&
      (selectedCurator === "all" || solution.curator === selectedCurator) &&
      (
        solution.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.curator.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  });

  const addToCart = (solution: Solution) => {
    if (!cart.some(item => item.id === solution.id)) {
      setCart([...cart, solution]);
    }
  };

  const removeFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const getCommercialTypeColor = (type: string) => {
    switch (type) {
      case "Sale":
        return "bg-blue-100 text-blue-800";
      case "Lease":
        return "bg-green-100 text-green-800";
      case "Collaborate":
        return "bg-purple-100 text-purple-800";
      case "Stake":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryColor = (category: string) => {
    if (category.includes("Construction")) return "bg-red-100 text-red-800";
    if (category.includes("Project")) return "bg-blue-100 text-blue-800";
    if (category.includes("Engineering")) return "bg-indigo-100 text-indigo-800";
    if (category.includes("Green")) return "bg-green-100 text-green-800";
    if (category.includes("Automation")) return "bg-purple-100 text-purple-800";
    if (category.includes("Health")) return "bg-orange-100 text-orange-800";
    if (category.includes("Data")) return "bg-cyan-100 text-cyan-800";
    if (category.includes("AI")) return "bg-violet-100 text-violet-800";
    if (category.includes("Machine")) return "bg-fuchsia-100 text-fuchsia-800";
    if (category.includes("Upstream")) return "bg-emerald-100 text-emerald-800";
    if (category.includes("Downstream")) return "bg-amber-100 text-amber-800";
    if (category.includes("Midstream")) return "bg-teal-100 text-teal-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black">{title}</h1>
            <p className="text-gray-600 mt-2">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Commercial Type</label>
                    <Select value={commercialType} onValueChange={setCommercialType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Sale">Sale</SelectItem>
                        <SelectItem value="Lease">Lease</SelectItem>
                        <SelectItem value="Collaborate">Collaborate</SelectItem>
                        <SelectItem value="Stake">Stake</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <ListFilter className="h-4 w-4" />
                      Category
                    </label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category === "all" ? "All Categories" : category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      Curator
                    </label>
                    <Select value={selectedCurator} onValueChange={setSelectedCurator}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Curator" />
                      </SelectTrigger>
                      <SelectContent>
                        {curators.map((curator) => (
                          <SelectItem key={curator} value={curator}>
                            {curator === "all" ? "All Curators" : curator}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Your Cart ({cart.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {cart.length === 0 ? (
                    <p className="text-sm text-gray-500">Your cart is empty</p>
                  ) : (
                    <div className="space-y-4">
                      {cart.map((item) => (
                        <div key={item.id} className="flex justify-between items-center text-sm border-b pb-2">
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-gray-600">${item.price.toLocaleString()}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.id)}>
                            ×
                          </Button>
                        </div>
                      ))}
                      <div className="pt-2 border-t">
                        <div className="flex justify-between font-medium">
                          <span>Total:</span>
                          <span>${getTotalPrice().toLocaleString()}</span>
                        </div>
                        <Button className="w-full mt-4">
                          Checkout
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="md:col-span-9">
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="relative w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search solutions..." 
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant={viewType === "list" ? "default" : "outline"} 
                      onClick={() => setViewType("list")}
                      className="px-3"
                    >
                      List
                    </Button>
                    <Button 
                      variant={viewType === "grid" ? "default" : "outline"} 
                      onClick={() => setViewType("grid")}
                      className="px-3"
                    >
                      Grid
                    </Button>
                  </div>
                </div>

                {viewType === "list" && (
                  <div className="mt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Solution</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Curator</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSolutions.map((solution) => (
                          <TableRow key={solution.id}>
                            <TableCell className="font-medium">
                              {solution.title}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCategoryColor(solution.category)}>
                                {solution.category}
                              </Badge>
                            </TableCell>
                            <TableCell>{solution.curator}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                                {solution.commercialType}
                              </Badge>
                            </TableCell>
                            <TableCell>${solution.price.toLocaleString()}</TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                onClick={() => addToCart(solution)}
                                disabled={cart.some(item => item.id === solution.id)}
                              >
                                {cart.some(item => item.id === solution.id) ? 'Added' : 'Add to Cart'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {viewType === "grid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSolutions.map((solution) => (
                      <Card key={solution.id} className="overflow-hidden">
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <Badge variant="outline" className={getCategoryColor(solution.category)}>
                              {solution.category}
                            </Badge>
                            <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                              {solution.commercialType}
                            </Badge>
                          </div>
                          <h3 className="font-medium mb-1">{solution.title}</h3>
                          <p className="text-sm mb-4 text-gray-600 line-clamp-2">{solution.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">${solution.price.toLocaleString()}</span>
                            <Button 
                              size="sm" 
                              onClick={() => addToCart(solution)}
                              disabled={cart.some(item => item.id === solution.id)}
                            >
                              {cart.some(item => item.id === solution.id) ? 
                                'Added' : 
                                <PlusCircle className="h-4 w-4" />
                              }
                            </Button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">Curator: {solution.curator}</div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious href="#" />
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#" isActive>1</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#">2</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#">3</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext href="#" />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChannelSolutions;
